import { Link } from "react-router-dom";
import { motion, useInView, useMotionValue, animate } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { useLang } from "../lang";
import { HOME_MEDIA, LOGO_BADGE, LOGO_MAIN, PARTNERS, NUMBERS } from "../content";
import { MACROS } from "../catalog";
import BrandEyebrow from "../components/BrandEyebrow";
import HGWSymbol from "../components/HGWSymbol";
import CatalogRibbon from "../components/home/CatalogRibbon";
import ParallaxTile from "../components/home/ParallaxTile";

function useReveal() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-15% 0px" });
  return { ref, inView };
}

/* ------- Hero image ------- */
function HeroImage() {
  const ref = useRef(null);
  const src = typeof window !== "undefined" && window.innerWidth < 768 ? "/hero-video-960.mp4" : "/hero-video-1920.mp4";
  useEffect(() => {
    const v = ref.current;
    if (!v) return undefined;
    v.muted = true;
    v.play().catch(() => {});
    const io = new IntersectionObserver(([e]) => (e.isIntersecting ? v.play().catch(() => {}) : v.pause()));
    io.observe(v);
    return () => io.disconnect();
  }, []);
  return (
    <div className="relative w-full aspect-[16/8] md:aspect-[21/8] bg-white overflow-hidden select-none cursor-default" onContextMenu={(e) => e.preventDefault()} data-testid="hero-video-wrap">
      <video
        ref={ref}
        src={src}
        poster="/hero-video-poster.jpg"
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        disablePictureInPicture
        disableRemotePlayback
        controls={false}
        aria-hidden="true"
        className="w-full h-full object-cover object-[50%_45%]"
        data-testid="hero-video"
      />
      <div className="absolute inset-0 z-10" aria-hidden="true" />
    </div>
  );
}

/* ------- Hero (Apple-like centered) — LOGO + emotional tagline ------- */
function Hero() {
  const { t } = useLang();
  return (
    <section className="relative bg-white pt-28 md:pt-32 pb-0 overflow-hidden" data-testid="home-hero">
      <div className="max-w-[1200px] mx-auto px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
          className="flex items-center justify-center"
          data-testid="hero-logo-wrap"
        >
          <img
            src={LOGO_BADGE}
            alt="HGW Industry — High Grade Windows"
            className="h-20 md:h-28 lg:h-32 w-auto rounded-md shadow-[0_20px_50px_-20px_rgba(29,63,148,0.35)]"
            data-testid="hero-logo"
          />
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="mt-10 md:mt-14 text-[32px] md:text-[52px] lg:text-[64px] font-semibold tracking-[-0.03em] leading-[1.05] text-[#1d1d1f] max-w-[900px] mx-auto"
          data-testid="hero-title"
        >
          {t.heroTitle}
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.35 }}
          className="mt-4 text-[15px] md:text-[18px] text-[#1d1d1f]/60 tracking-[-0.01em] max-w-[620px] mx-auto"
          data-testid="hero-sub"
        >
          {t.heroSub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-8 flex items-center justify-center gap-6 flex-wrap"
        >
          <Link to="/prodotti" className="btn-pill btn-primary" data-testid="hero-cta-products">
            {t.heroCtaProducts}
          </Link>
          <Link to="/contatti" className="a-link" data-testid="hero-cta-contact">
            {t.heroCtaContact} <span className="chev">›</span>
          </Link>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.1, delay: 0.4, ease: [0.19, 1, 0.22, 1] }}
        className="mt-16 md:mt-24"
      >
        <div className="max-w-[1400px] mx-auto">
          <HeroImage />
        </div>
      </motion.div>
    </section>
  );
}

/* ------- Company intro (storytelling) ------- */
function CompanyIntro() {
  const { t } = useLang();
  const { ref, inView } = useReveal();
  return (
    <section id="chi-siamo" ref={ref} className="section-pad bg-white" data-testid="home-intro">
      <div className="max-w-[900px] mx-auto px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-5"
        >
          <BrandEyebrow>{t.introEyebrow}</BrandEyebrow>
        </motion.div>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.05 }}
          className="text-[32px] md:text-[48px] lg:text-[56px] font-semibold tracking-[-0.02em] leading-[1.08] text-[#1d1d1f]"
        >
          {t.homeIntroTitle}
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="mt-6 text-[17px] md:text-[19px] text-[#1d1d1f]/80 leading-[1.6]"
          data-testid="intro-p1"
        >
          {t.homeIntroBody}
        </motion.p>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.25 }}
          className="mt-5 text-[17px] md:text-[19px] text-[#1d1d1f]/80 leading-[1.6]"
          data-testid="intro-p2"
        >
          {t.homeIntroBody2}
        </motion.p>
      </div>
    </section>
  );
}

/* ------- Media gallery ------- */
function MediaShowcase() {
  const { t, lang } = useLang();
  const { ref, inView } = useReveal();
  return (
    <section ref={ref} className="section-pad bg-[#f5f5f7]" data-testid="home-media">
      <div className="max-w-[1200px] mx-auto px-6 text-center">
        <div className="text-[13px] uppercase tracking-widest text-[#1D3F94] mb-4">{t.mediaEyebrow}</div>
        <h2 className="text-[32px] md:text-[48px] font-semibold tracking-[-0.02em] text-[#1d1d1f]">{t.mediaTitle}</h2>
        <p className="mt-4 text-[17px] text-[#1d1d1f]/80 max-w-[680px] mx-auto">{t.mediaBody}</p>
      </div>

      <div className="mt-12 max-w-[1400px] mx-auto px-6 grid grid-cols-12 gap-4 md:gap-5">
        {HOME_MEDIA.map((m, i) => (
          <motion.figure
            key={m.id}
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.05 + i * 0.08 }}
            className={`relative overflow-hidden rounded-3xl bg-black ${i === 0 ? "col-span-12 md:col-span-8 h-[280px] md:h-[420px]" : i === 1 ? "col-span-12 md:col-span-4 h-[280px] md:h-[420px]" : "col-span-6 md:col-span-6 h-[220px] md:h-[320px]"}`}
            data-testid={`media-${m.id}`}
          >
            <img src={m.src} alt={m.title[lang]} className="w-full h-full object-cover" loading="lazy" />
            <figcaption className="absolute left-5 bottom-5 text-white text-[14px] font-medium">
              {m.title[lang]}
            </figcaption>
          </motion.figure>
        ))}
      </div>
    </section>
  );
}

/* ------- Numbers showcase — animated count-up KPIs ------- */
function CountUp({ to, suffix = "", start }) {
  const [display, setDisplay] = useState(0);
  const mv = useMotionValue(0);
  useEffect(() => {
    if (!start) return;
    const controls = animate(mv, to, {
      duration: 2.2,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return controls.stop;
  }, [start, to, mv]);
  const formatted = display >= 1000
    ? display.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")
    : display;
  return <span>{formatted}{suffix}</span>;
}

function NumbersShowcase() {
  const { t, lang } = useLang();
  const { ref, inView } = useReveal();
  return (
    <section ref={ref} className="section-pad bg-[#f5f5f7]" data-testid="home-numbers">
      <div className="max-w-[1200px] mx-auto px-6 text-center">
        <div className="mb-5"><BrandEyebrow>{t.numbersEyebrow}</BrandEyebrow></div>
        <h2 className="text-[32px] md:text-[48px] font-semibold tracking-[-0.02em] text-[#1d1d1f] leading-[1.1]">
          {t.numbersTitle}
        </h2>
        <p className="mt-4 text-[16px] md:text-[18px] text-[#1d1d1f]/70 max-w-[640px] mx-auto leading-relaxed">
          {t.numbersBody}
        </p>
      </div>

      <div className="mt-14 md:mt-20 max-w-[1200px] mx-auto px-6 grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
        {NUMBERS.map((n, i) => (
          <motion.div
            key={n.id}
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.1 + i * 0.1, ease: [0.19, 1, 0.22, 1] }}
            className="relative bg-white rounded-3xl p-8 md:p-10 overflow-hidden group text-center"
            data-testid={`number-${n.id}`}
          >
            <div className="relative flex flex-col items-center">
              <div className="text-[44px] md:text-[64px] lg:text-[72px] font-semibold tracking-[-0.03em] text-[#1D3F94] leading-none">
                <CountUp to={n.value} suffix={n.suffix} start={inView} />
              </div>
              <div className="mt-4 text-[13px] md:text-[14px] text-[#1d1d1f]/80 leading-snug font-medium">
                {lang === "it" ? n.it : n.en}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/* ------- Mission / Vision (manifesto style — separate phrases) ------- */
function MissionVision() {
  const { t } = useLang();
  const { ref, inView } = useReveal();
  return (
    <section ref={ref} className="section-pad bg-white" data-testid="home-mv">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="text-center max-w-[1040px] mx-auto">
          <div className="mb-5"><BrandEyebrow>{t.mvEyebrow}</BrandEyebrow></div>
          <h2 className="text-[32px] md:text-[48px] font-semibold tracking-[-0.02em] text-[#1d1d1f]">{t.mvTitle}</h2>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {[{ label: t.missionLabel, body: t.homeMissionBody || t.missionBody, dark: false, id: "mv-mission" },
            { label: t.visionLabel, body: t.homeVisionBody || t.visionBody, dark: true, id: "mv-vision" }].map((b, i) => (
            <motion.div
              key={b.id}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.1 + i * 0.1 }}
              className={`rounded-3xl p-8 md:p-12 ${b.dark ? "bg-[#0b0f1a] text-white" : "bg-[#f5f5f7] text-[#1d1d1f]"}`}
              data-testid={b.id}
            >
              <div className={`text-[12px] uppercase tracking-widest mb-6 ${b.dark ? "text-white/60" : "text-[#6e6e73]"}`}>
                {b.label}
              </div>
              <ul className="space-y-4 max-w-[440px]">
                {(Array.isArray(b.body) ? b.body : [b.body]).map((line, li) => (
                  <motion.li
                    key={li}
                    initial={{ opacity: 0, x: -8 }}
                    animate={inView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.6, delay: 0.25 + i * 0.08 + li * 0.08 }}
                    className={`flex items-start gap-3 text-[17px] md:text-[19px] leading-[1.45] font-medium tracking-[-0.005em] ${b.dark ? "text-white" : "text-[#1d1d1f]"}`}
                  >
                    <HGWSymbol size={16} className="mt-[5px] shrink-0 text-[#1D3F94]" />
                    <span>{line}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------- Partner Brands section ------- */
function PartnerBrands() {
  const { t, lang } = useLang();
  const { ref, inView } = useReveal();
  return (
    <section
      ref={ref}
      className="section-pad bg-white border-t border-[#e4e4e7]"
      data-testid="home-partners"
    >
      <div className="max-w-[1100px] mx-auto px-6">
        {/* HGW logo blu + eyebrow */}
        <div className="text-center">
          <motion.img
            initial={{ opacity: 0, y: 12 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7 }}
            src={LOGO_MAIN}
            alt="HGW Industry"
            className="h-8 md:h-10 w-auto mx-auto"
            data-testid="partners-hgw-logo"
          />
          <motion.div
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-6 text-[11px] md:text-[12px] uppercase tracking-[0.24em] text-[#6e6e73]"
          >
            {t.partnersEyebrow}
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-3 text-[28px] md:text-[40px] font-semibold tracking-[-0.02em] text-[#1d1d1f]"
          >
            {t.partnersTitle}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-3 text-[15px] md:text-[17px] text-[#1d1d1f]/70 max-w-[600px] mx-auto"
          >
            {t.partnersBody}
          </motion.p>
        </div>

        {/* Two partner reference cards (non-clickable) */}
        <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {PARTNERS.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 24 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.5 + i * 0.12 }}
              className="relative overflow-hidden rounded-3xl bg-[#f5f5f7] px-8 py-16 md:py-20 flex flex-col items-center justify-center text-center"
              data-testid={`partner-${p.id}`}
            >
              <div className="text-[36px] md:text-[52px] lg:text-[60px] font-semibold tracking-[0.02em] text-[#1d1d1f]">
                {p.name}
              </div>
              <div className="mt-4 text-[13px] md:text-[15px] text-[#6e6e73] max-w-[380px]">
                {lang === "it" ? p.it : p.en}
              </div>
              {p.badgeIt && (
                <div
                  className="mt-6 inline-flex items-center px-3 py-1.5 rounded-full text-[11px] font-medium tracking-[0.06em] uppercase bg-[#1D3F94] text-white"
                  data-testid={`partner-${p.id}-badge`}
                >
                  {lang === "it" ? p.badgeIt : p.badgeEn}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------- Products preview (2 macros as big tiles) ------- */
function ProductsPreview() {
  const { t, lang } = useLang();
  const { ref } = useReveal();

  return (
    <section id="prodotti-preview" ref={ref} className="section-pad bg-[#f5f5f7]" data-testid="home-products">
      <div className="max-w-[1200px] mx-auto px-6 text-center">
        <div className="mb-5"><BrandEyebrow>{t.productsEyebrow}</BrandEyebrow></div>
        <h2 className="text-[32px] md:text-[48px] font-semibold tracking-[-0.02em] text-[#1d1d1f]">{t.homeProductsTitle}</h2>
        <p className="mt-4 text-[17px] text-[#1d1d1f]/80 max-w-[720px] mx-auto">{t.homeProductsBody}</p>
      </div>

      <div className="mt-12 max-w-[1200px] mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
        {Object.values(MACROS).map((m, i) => (
          <ParallaxTile
            key={m.slug}
            to={`/prodotti/${m.slug}`}
            testId={`home-macro-${m.slug}`}
            image={m.image}
            title={m[lang].title}
            subtitle={{ architettonici: t.catArchSub, pergole: t.catPergoleSub }[m.slug] || m[lang].sub}
            index={i}
          />
        ))}
      </div>
    </section>
  );
}

/* ------- Contact CTA ------- */
function ContactCTA() {
  const { t, lang } = useLang();
  return (
    <section className="section-pad bg-[#0b0f1a] text-white text-center" data-testid="home-contact-cta">
      <div className="max-w-[820px] mx-auto px-6">
        <h2 className="text-[32px] md:text-[48px] font-semibold tracking-[-0.02em]">
          {t.homeContactTitle}
        </h2>
        <p className="mt-4 text-[17px] text-white/80">
          {t.homeContactBody}
        </p>
        <div className="mt-8 flex items-center justify-center gap-6 flex-wrap">
          <Link to="/contatti" className="btn-pill btn-primary" data-testid="home-cta-contact">
            {lang === "it" ? "Contattaci" : "Contact us"}
          </Link>
          <Link to="/cataloghi" className="a-link !text-white" data-testid="home-cta-catalogs">
            {lang === "it" ? "Sfoglia i cataloghi" : "Browse catalogues"} <span className="chev">›</span>
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main data-testid="home-page" className="bg-white">
      <Hero />
      <ProductsPreview />
      <CatalogRibbon />
      <CompanyIntro />
      <NumbersShowcase />
      <MissionVision />
      <PartnerBrands />
      <ContactCTA />
    </main>
  );
}
